import { LIQUIDITY_RULE } from '../../../utils/liquidityRule';

// Types for Advanced Filter Modal

// Distribution bucket for histogram visualization
export interface DistributionBucket {
  min: number;
  max: number | null;
  count: number;
  percentage: number;
}

// API response for distribution data
export interface FilterDistributionData {
  iv_rank: DistributionBucket[];
  annualized_yield: DistributionBucket[];
  spread_pct: DistributionBucket[];
  net_margin: DistributionBucket[];
  debt_equity: DistributionBucket[];
  dividend_yield: DistributionBucket[];
  rsi: DistributionBucket[];
  baseline_pnl: DistributionBucket[];
}

export interface DistributionResponse {
  status: string;
  total_stocks: number;
  distributions: FilterDistributionData;
  sector_counts: Record<string, number>;
  market_cap_counts: Record<string, number>;
  trend_counts: Record<string, number>;
  spread_counts: Record<string, number>;
}

// Advanced filter state
export interface AdvancedFilters {
  // Section A: Option Liquidity & Returns
  iv_rank_min?: number;
  iv_rank_max?: number;
  annualized_yield_min?: number;
  annualized_yield_max?: number;
  spread_pct_min?: number;
  spread_pct_max?: number;

  // Section B: Company Fundamentals
  market_cap_categories?: string[]; // 'mega', 'large', 'mid', 'small', 'micro'
  sectors?: string[]; // High-level categories
  net_margin_min?: number;
  net_margin_max?: number;
  debt_equity_max?: number;
  dividend_yield_min?: number;
  dividend_yield_max?: number;
  // Earnings filter
  earnings_within_days?: number;          // Show stocks with earnings within X days
  exclude_earnings_within_days?: number;  // Exclude stocks with earnings within X days

  // Section C: Technical Indicators
  rsi_min?: number;
  rsi_max?: number;
  trend_signals?: string[]; // 'Bullish', 'Golden Cross', 'Bearish', etc.

  // New filters
  slope_12w_min?: number;
  slope_12w_max?: number;
  macd_signals?: string[];  // 'Rising', 'Bull Cross'
  open_interest_min?: number;
  open_interest_max?: number;
  baseline_pnl_min?: number;
  baseline_pnl_max?: number;
  min_dimension1_stars?: number;  // 1-5 stars, maps to score >= stars*10
  min_dimension2_stars?: number;  // 1-5 stars, maps to score >= stars*10
  setup_signals?: string[];  // 'good_setup', 'acceptable', 'tactical', 'unfavorable'
}

// Component Props
export interface AdvancedFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: AdvancedFilters) => void;
  initialFilters?: AdvancedFilters;
}

export interface RangeSliderWithHistogramProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  /** Omit when there is no distribution data: the slider renders without a histogram. */
  distribution?: DistributionBucket[];
  formatValue?: (value: number) => string;
  highlightRange?: [number, number];
  highlightColor?: string;
  unit?: string;
  tooltip?: string;
}

export interface CollapsibleFilterSectionProps {
  title: string | React.ReactNode;
  /** Quiet qualifier after the title, e.g. "not scored". */
  note?: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  activeFilterCount?: number;
  children: React.ReactNode;
}

export interface CheckboxFilterGroupProps {
  options: Array<{ value: string; label: string; count?: number }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  columns?: 1 | 2 | 3;
}

// Permanent filter sidebar props (desktop)
export interface FilterSidebarProps {
  filters: AdvancedFilters;
  onChange: (filters: AdvancedFilters) => void;
  resultCount: number | null;
  isLoadingCount?: boolean;
  // Preset support
  activePresetId?: number | null;
  onSelectPreset?: (presetId: number | null) => void;
  onSavePreset?: () => void;
  // Modification tracking
  isPresetModified?: boolean;
  basePresetId?: number | null;
}

// Responsive wrapper props (switches between sidebar and FAB+drawer)
export interface FilterSidebarWrapperProps {
  filters: AdvancedFilters;
  onChange: (filters: AdvancedFilters) => void;
  resultCount: number | null;
  isLoadingCount?: boolean;
  // Preset support
  activePresetId?: number | null;
  onSelectPreset?: (presetId: number | null) => void;
  onSavePreset?: () => void;
  // Modification tracking
  isPresetModified?: boolean;
  basePresetId?: number | null;
}

// Default filters for covered call screening
// These provide sensible starting values for beginners
// Below 3 stars on either dimension should be avoided
export const COVERED_CALL_DEFAULT_FILTERS: AdvancedFilters = {
  // Liquidity rule defaults (utils/liquidityRule.ts); users can loosen them.
  // Getters: the rule is loaded from the backend after this module is imported.
  get spread_pct_max() { return LIQUIDITY_RULE.maxSpreadPct; },
  get open_interest_min() { return LIQUIDITY_RULE.minOpenInterest; },
  exclude_earnings_within_days: 7,
  min_dimension1_stars: 3,
  min_dimension2_stars: 3,
};
